import React from 'react';
import { Clock3, Repeat2, Trophy } from 'lucide-react';
import { formatCompactInteger } from '../utils/numberFormat';
import { formatClockDuration } from '../utils/timeUtils';
import { t } from '../i18n';
import './MonthlySummary.css';

/**
 * Compact summary for a month or selected date range. Daily averages use
 * total calendar days in the period (up to today), including inactive days.
 * 
 * @param {Object} props
 * @param {Object} props.summary - Monthly summary data
 * @param {number} props.summary.practicedDays - Count of days with practice
 * @param {number} props.summary.totalSeconds - Total practice time in seconds
 * @param {number} props.summary.totalRepeatCount - Total repeat count
 * @param {number} props.summary.totalScoreGained - Total score gained
 */
function MonthlySummary({ summary, title = t('accountActivity.calendar.monthlySummary') }) {
  if (!summary) {
    return null;
  }

  const {
    practicedDays,
    totalSeconds,
    totalRepeatCount,
    totalScoreGained = 0,
    totalDays = practicedDays || 0,
  } = summary;

  const breakdown = calculateMonthlyBreakdown({
    practicedDays,
    totalDays,
    totalSeconds,
    totalRepeatCount,
    totalScoreGained,
  });

  const rows = [
    { label: t('accountActivity.stats.score'), total: formatCompactInteger(totalScoreGained), average: formatCompactInteger(breakdown.averageScorePerDay), icon: Trophy },
    { label: t('accountActivity.calendar.repeatLabel'), total: formatCompactInteger(totalRepeatCount), average: formatCompactInteger(breakdown.averageRepeatCountPerDay), icon: Repeat2 },
    { label: t('accountActivity.calendar.timeLabel'), total: formatClockDuration(totalSeconds), average: formatClockDuration(breakdown.averageSecondsPerDay), icon: Clock3 },
  ];

  return (
    <div className="monthly-summary">
      <h3 className="monthly-summary-title">{title}</h3>
      <p className="monthly-summary-attendance">
        <span>{t('accountActivity.stats.practiceDays')} <strong>{formatCompactInteger(practicedDays)}/{formatCompactInteger(breakdown.totalDays)}</strong></span>
        <span>{t('accountActivity.stats.practiceRate')} <strong>{formatCompactInteger(breakdown.practiceRatePercent)}%</strong></span>
      </p>
      <table className="monthly-summary-table" aria-label={title}>
        <thead><tr><th scope="col"><span className="summary-visually-hidden">{t('accountActivity.calendar.metric')}</span></th><th scope="col">{t('accountActivity.calendar.total')}</th><th scope="col">{t('accountActivity.calendar.average')}</th></tr></thead>
        <tbody>{rows.map(({ label, total, average, icon: Icon }) => (
          <tr key={label}><th scope="row"><span><Icon size={17} aria-hidden="true" />{label}</span></th><td>{total}</td><td>{average}</td></tr>
        ))}</tbody>
      </table>
      <p className="monthly-summary-average-note">{t('accountActivity.calendar.averageBasis')}</p>
    </div>
  );
}

function calculateMonthlyBreakdown({
  practicedDays = 0,
  totalDays = practicedDays || 0,
  totalSeconds = 0,
  totalRepeatCount = 0,
  totalScoreGained = 0,
}) {
  const safeTotalDays = Math.max(0, Number(totalDays) || 0);
  const safePracticedDays = Math.max(0, Number(practicedDays) || 0);

  if (safeTotalDays === 0) {
    return {
      totalDays: 0,
      practiceRatePercent: 0,
      averageScorePerDay: 0,
      averageSecondsPerDay: 0,
      averageRepeatCountPerDay: 0,
    };
  }

  return {
    totalDays: safeTotalDays,
    practiceRatePercent: (safePracticedDays / safeTotalDays) * 100,
    averageScorePerDay: (Number(totalScoreGained) || 0) / safeTotalDays,
    averageSecondsPerDay: (Number(totalSeconds) || 0) / safeTotalDays,
    averageRepeatCountPerDay: (Number(totalRepeatCount) || 0) / safeTotalDays,
  };
}

/**
 * Calculate average statistics per practiced day
 * Requirements 1.1, 1.2, 1.3, 1.4, 1.5
 * 
 * @param {number} practicedDays - Count of days with practice
 * @param {number} totalSeconds - Total practice time in seconds
 * @param {number} totalRepeatCount - Total repeat count
 * @returns {Object} Average statistics
 */
function calculateAverages(practicedDays, totalSeconds, totalRepeatCount) {
  // Handle division by zero (Requirement 1.5)
  if (practicedDays === 0) {
    return {
      averagePracticedDaysPerDay: 0,
      averageSecondsPerDay: 0,
      averageRepeatCountPerDay: 0
    };
  }

  return {
    // Always 1.0 for monthly view (Requirement 1.1)
    averagePracticedDaysPerDay: 1.0,
    // Average practice time per practiced day (Requirement 1.2, 1.4)
    averageSecondsPerDay: totalSeconds / practicedDays,
    // Average repeat count per practiced day (Requirement 1.3, 1.4)
    averageRepeatCountPerDay: totalRepeatCount / practicedDays
  };
}

/**
 * Format seconds into human-readable time string
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time (e.g., "30m", "1h 15m", "7h 30m")
 */
function formatTime(seconds, useDecimal = false) {
  const unit = (key, count) => t(`accountActivity.stats.${key}Value`, undefined, { count });
  const safeSeconds = Math.max(0, Number(seconds) || 0);

  if (safeSeconds === 0) {
    return unit('minutes', 0);
  }

  if (safeSeconds < 60) {
    return unit('seconds', useDecimal ? parseFloat(safeSeconds.toFixed(1)) : Math.round(safeSeconds));
  }

  const totalMinutes = safeSeconds / 60;
  const hours = Math.floor(totalMinutes / 60);

  if (hours === 0) {
    if (useDecimal) {
      return unit('minutes', parseFloat(totalMinutes.toFixed(1)));
    }
    return unit('minutes', Math.floor(totalMinutes));
  }

  const remainingMinutes = useDecimal
    ? parseFloat((totalMinutes % 60).toFixed(1))
    : Math.floor(totalMinutes % 60);

  if (remainingMinutes === 0) {
    return unit('hours', hours);
  }

  return `${unit('hours', hours)} ${unit('minutes', remainingMinutes)}`;
}

export default MonthlySummary;
export { formatTime, calculateAverages, calculateMonthlyBreakdown };
